var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import pMap from "p-map";
import { ASSET_CONFIG_CHUNK_SIZE, assetConfigBoxMbr, DELETE_BOXES_CHUNK_SIZE, SEND_PARAMS, TOKEN_CLAIM_BOX_MBR, WHITELIST_BOX_MBR, WHITELIST_CHUNK_SIZE, } from "./constants";
import { BirthdayNftDispenserClient } from "./generated/BirthdayNftDispenserClient";
import { DispenserReaderSDK } from "./sdkReader";
import { chunk } from "./util/chunk";
import { requireWriter } from "./util/requiresSender";
import { createTxnExecutor } from "./util/txnExecutor";
import { errorTransformer, wrapErrors, wrapErrorsInternal } from "./util/wrapErrors";
/**
 * Writer SDK for the BirthdayNftDispenser contract.
 *
 * Every write method comes in two flavours:
 * - makeXYZTxns(...): returns the typed client composer (builder) without sending,
 *   so callers can compose/modify groups. Accepts an optional `builder` to chain.
 * - xyz(...): executor that builds, auto-increases opcode budget if needed,
 *   sends, and transforms ARC-65 error codes into rich errors.
 */
export class DispenserSDK extends DispenserReaderSDK {
    writerAccount;
    /** Write client (defaultSender/defaultSigner = writerAccount) */
    writeClient;
    makeTxnExecutor = createTxnExecutor(this, () => this.writeClient.newGroup(), wrapErrorsInternal, () => this.writerAccount, () => this.algorand.client.algod);
    constructor({ writerAccount, ...rest }) {
        super(rest);
        this.writerAccount = writerAccount;
        if (writerAccount) {
            this.writeClient = new BirthdayNftDispenserClient({
                algorand: this.algorand,
                appId: this.appId,
                defaultSender: writerAccount.sender,
                defaultSigner: writerAccount.signer,
            });
        }
        this.algorand.registerErrorTransformer(errorTransformer);
    }
    // Admin: role management
    /**
     * Two-txn admin transfer: setAdmin1 sent by the current admin (writerAccount),
     * setAdmin2 sent by the new admin in the same group — both must sign.
     */
    makeSetAdminTxns({ newAdmin, newAdminSigner, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        return builder.setAdmin1({ args: { newAdmin }, note }).setAdmin2({ args: {}, sender: newAdmin, signer: newAdminSigner });
    }
    setAdmin = this.makeTxnExecutor({ maker: this.makeSetAdminTxns, sendParams: SEND_PARAMS });
    // Admin: asset & treasury management
    makeOptinAssetTxns({ assetId, note, builder }) {
        builder = builder ?? this.writeClient.newGroup();
        // 1 inner asset opt-in
        return builder.optinAsset({ args: { assetId }, note, extraFee: (1000).microAlgo() });
    }
    optinAsset = this.makeTxnExecutor({ maker: this.makeOptinAssetTxns, sendParams: SEND_PARAMS });
    makeCloseoutAssetTxns({ assetId, closeTo, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        // 1 inner asset close-out
        return builder.closeoutAsset({ args: { assetId, closeTo }, note, extraFee: (1000).microAlgo() });
    }
    closeoutAsset = this.makeTxnExecutor({ maker: this.makeCloseoutAssetTxns, sendParams: SEND_PARAMS });
    makeWithdrawAlgoTxns({ receiver, amount, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        // 1 inner payment
        return builder.withdrawAlgo({ args: { receiver, amount }, note, extraFee: (1000).microAlgo() });
    }
    withdrawAlgo = this.makeTxnExecutor({ maker: this.makeWithdrawAlgoTxns, sendParams: SEND_PARAMS });
    makeWithdrawAssetTxns({ assetId, receiver, amount, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        // 1 inner asset transfer
        return builder.withdrawAsset({ args: { assetId, receiver, amount }, note, extraFee: (1000).microAlgo() });
    }
    withdrawAsset = this.makeTxnExecutor({ maker: this.makeWithdrawAssetTxns, sendParams: SEND_PARAMS });
    // Admin: asset config management
    async makeAddAssetConfigTxns({ assetId, startTime = 0n, endTime, claimTokenPrefix, mbrPayment = true, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        if (mbrPayment) {
            // cover the config box MBR
            builder.addTransaction(await this.algorand.createTransaction.payment({
                sender: this.writerAccount.sender,
                receiver: this.appAddress,
                amount: assetConfigBoxMbr(claimTokenPrefix.length).microAlgo(),
            }), this.writerAccount.signer);
        }
        return builder.addAssetConfig({ args: { assetId, startTime, endTime, claimTokenPrefix }, note });
    }
    addAssetConfig = this.makeTxnExecutor({ maker: this.makeAddAssetConfigTxns, sendParams: SEND_PARAMS });
    makeSetAssetClaimCountTxns({ assetId, claimCount, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        // box size is unchanged (claimCount is a fixed-width uint64), so no MBR payment
        return builder.setAssetClaimCount({ args: { assetId, claimCount }, note });
    }
    setAssetClaimCount = this.makeTxnExecutor({ maker: this.makeSetAssetClaimCountTxns, sendParams: SEND_PARAMS });
    makeSetAssetStartTimeTxns({ assetId, startTime, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        // box size is unchanged (startTime is a fixed-width uint64), so no MBR payment
        return builder.setAssetStartTime({ args: { assetId, startTime }, note });
    }
    /**
     * Set the Unix timestamp (seconds) before which claims of `assetId` are rejected
     * (admin only). Pass 0n to remove the start time (claims are open immediately).
     * Requires an existing asset config; the box size is unchanged, so no MBR payment
     * is needed.
     */
    setAssetStartTime = this.makeTxnExecutor({ maker: this.makeSetAssetStartTimeTxns, sendParams: SEND_PARAMS });
    makeSetAssetEndTimeTxns({ assetId, endTime, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        // box size is unchanged (endTime is a fixed-width uint64), so no MBR payment
        return builder.setAssetEndTime({ args: { assetId, endTime }, note });
    }
    /**
     * Set the Unix timestamp (seconds) after which claims of `assetId` are rejected
     * (admin only). Pass 0n to remove the end time (claims never expire). Requires an
     * existing asset config; the box size is unchanged, so no MBR payment is needed.
     */
    setAssetEndTime = this.makeTxnExecutor({ maker: this.makeSetAssetEndTimeTxns, sendParams: SEND_PARAMS });
    makeDeleteAssetConfigsTxns({ assetIds, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        return builder.deleteAssetConfigs({ args: { assetIds }, note });
    }
    deleteAssetConfigsChunk = this.makeTxnExecutor({
        maker: this.makeDeleteAssetConfigsTxns,
        sendParams: SEND_PARAMS,
    });
    /**
     * Delete asset config boxes, freeing their MBR back to the app balance (admin only).
     * Once deleted an asset is unregistered: claim and the readonly getters fail for it
     * until it is re-added via addAssetConfig (which assigns a fresh numeric id, so stale
     * whitelist / claimedTokens entries never rebind). Does not touch whitelist or
     * claimedTokens boxes. Auto-chunks into groups of ASSET_CONFIG_CHUNK_SIZE (box
     * reference limit); each chunk is all-or-nothing.
     */
    async deleteAssetConfigs({ assetIds, ...rest }) {
        const chunks = chunk(assetIds.map((id) => BigInt(id)), ASSET_CONFIG_CHUNK_SIZE);
        return pMap(chunks, (chunkIds) => this.deleteAssetConfigsChunk({ assetIds: chunkIds, ...rest }), {
            concurrency: this.concurrency,
        });
    }
    // Admin: whitelist management
    async makeAddWhitelistTxns({ accounts, assetId, mbrPayment = true, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        if (mbrPayment && accounts.length > 0) {
            // cover the box MBR for each new whitelist entry (overpays slightly for appends)
            builder.addTransaction(await this.algorand.createTransaction.payment({
                sender: this.writerAccount.sender,
                receiver: this.appAddress,
                amount: (WHITELIST_BOX_MBR * BigInt(accounts.length)).microAlgo(),
            }), this.writerAccount.signer);
        }
        return builder.addWhitelist({ args: { accounts, assetId }, note });
    }
    addWhitelistChunk = this.makeTxnExecutor({ maker: this.makeAddWhitelistTxns, sendParams: SEND_PARAMS });
    /**
     * Whitelist accounts for an asset. Automatically chunks into groups of
     * WHITELIST_CHUNK_SIZE (box reference limit) and includes a payment per chunk
     * covering box MBR (disable with mbrPayment: false).
     */
    async addWhitelist({ accounts, ...rest }) {
        const chunks = chunk(accounts, WHITELIST_CHUNK_SIZE);
        return pMap(chunks, (chunkAccounts) => this.addWhitelistChunk({ accounts: chunkAccounts, ...rest }), {
            concurrency: this.concurrency,
        });
    }
    makeRemoveWhitelistTxns({ accounts, assetId, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        return builder.removeWhitelist({ args: { accounts, assetId }, note });
    }
    removeWhitelistChunk = this.makeTxnExecutor({ maker: this.makeRemoveWhitelistTxns, sendParams: SEND_PARAMS });
    /**
     * Remove each account's whitelist entry for an asset (boxes deleted when the
     * last entry goes, freeing MBR back to the app balance). Chunks like addWhitelist.
     */
    async removeWhitelist({ accounts, ...rest }) {
        const chunks = chunk(accounts, WHITELIST_CHUNK_SIZE);
        return pMap(chunks, (chunkAccounts) => this.removeWhitelistChunk({ accounts: chunkAccounts, ...rest }), {
            concurrency: this.concurrency,
        });
    }
    /** Builder for one deleteBoxes call: deletes each account's whitelist and claimedTokens box. */
    makeDeleteBoxesTxns({ accounts, note, builder }) {
        builder = builder ?? this.writeClient.newGroup();
        return builder.deleteBoxes({ args: { accounts }, note });
    }
    deleteBoxesChunk = this.makeTxnExecutor({ maker: this.makeDeleteBoxesTxns, sendParams: SEND_PARAMS });
    /**
     * Delete each account's per-account boxes (its whitelist box, spent or not, AND its
     * claimedTokens box if present), freeing both box MBRs back to the app balance.
     * Fails if an account has neither box (all-or-nothing per chunk). Auto-chunks into
     * groups of DELETE_BOXES_CHUNK_SIZE (each account costs 2 box references).
     *
     * WARNING: deleting a claimedTokens box re-enables token claiming of those assets for
     * that account - only run this for teardown after the relevant claim windows close.
     */
    async deleteBoxes({ accounts, ...rest }) {
        const chunks = chunk(accounts, DELETE_BOXES_CHUNK_SIZE);
        return pMap(chunks, (chunkAccounts) => this.deleteBoxesChunk({ accounts: chunkAccounts, ...rest }), {
            concurrency: this.concurrency,
        });
    }
    // Public
    /** Whether `sender` already holds (is opted into) `assetId`. 404 => not opted in. */
    async senderHoldsAsset(sender, assetId) {
        try {
            await this.algorand.client.algod.accountAssetInformation(sender, BigInt(assetId)).do();
            return true;
        }
        catch (e) {
            const status = e?.status;
            if (status === 404 || `${e?.message ?? e}`.toLowerCase().includes("not found"))
                return false;
            throw e;
        }
    }
    async makeClaimTxns({ assetId, token, mbrPayment = true, optInIfNeeded = true, note, builder, boxReferences, assetReferences, }) {
        builder = builder ?? this.writeClient.newGroup();
        if (optInIfNeeded && !(await this.senderHoldsAsset(this.writerAccount.sender.toString(), assetId))) {
            // claimer opts themselves in (0-amount self transfer) so claim's isOptedIn check passes
            builder.addTransaction(await this.algorand.createTransaction.assetTransfer({
                sender: this.writerAccount.sender,
                receiver: this.writerAccount.sender,
                assetId: BigInt(assetId),
                amount: 0n,
            }), this.writerAccount.signer);
        }
        if (token && token.length > 0 && mbrPayment) {
            // cover the claimedTokens box MBR so token claims don't erode the app's spendable balance
            builder.addTransaction(await this.algorand.createTransaction.payment({
                sender: this.writerAccount.sender,
                receiver: this.appAddress,
                amount: TOKEN_CLAIM_BOX_MBR.microAlgo(),
            }), this.writerAccount.signer);
        }
        // 1 inner asset transfer (the contract submits it with fee 0; caller pays).
        // Token claims also need ~1900 extra opcode budget for ed25519_verify_bare;
        // the executor adds an increaseBudget call automatically via simulation.
        return builder.claim({
            args: { assetId, token: token ?? new Uint8Array(0) },
            note,
            extraFee: (1000).microAlgo(),
            boxReferences,
            assetReferences,
        });
    }
    claim = this.makeTxnExecutor({ maker: this.makeClaimTxns, sendParams: SEND_PARAMS });
    // Admin: token claim configuration
    makeSetMidnightTokenSignerTxns({ tokenSigner, note, builder, }) {
        builder = builder ?? this.writeClient.newGroup();
        return builder.setMidnightTokenSigner({ args: { tokenSigner }, note });
    }
    setMidnightTokenSigner = this.makeTxnExecutor({ maker: this.makeSetMidnightTokenSignerTxns, sendParams: SEND_PARAMS });
}
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeSetAdminTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeOptinAssetTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeCloseoutAssetTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeWithdrawAlgoTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeWithdrawAssetTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeAddAssetConfigTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeSetAssetClaimCountTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeSetAssetStartTimeTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeSetAssetEndTimeTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeDeleteAssetConfigsTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeAddWhitelistTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeRemoveWhitelistTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeDeleteBoxesTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeClaimTxns", null);
__decorate([
    requireWriter(),
    wrapErrors()
], DispenserSDK.prototype, "makeSetMidnightTokenSignerTxns", null);
